import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import * as tableUtils from "../utils/table-utils";
import {
  filterableColumn,
  GridState,
  searchableSortableColumn,
  simpleColumn,
  sortableColumn
} from "../utils/table-utils";
import {
  COST_BEARER_OWN,
  COST_BEARER_RENTER,
  ErrorResponse,
  MaintenanceHistory,
  PageResponseEnvelope
} from "../api/dtos/dtos";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import {ErrorAlert} from "../common/ErrorAlert";
import furoApi from "../api/FuroApi";
import * as routerUtils from "../utils/router-utils";
import {Button, Table} from "antd";
import {ColumnProps} from "antd/lib/table";
import locales from "../locales";
import {formatDate} from "../utils/date-utils";
import {Link} from "react-router-dom";
import * as paths from "../paths";
import {renderPriceColumn} from "../utils/number-utils";
import ButtonGroup from "antd/lib/button/button-group";
import {renderItemName} from "../item/item-name-renderer";
import {
  showConfirm,
  showDeleteConfirm,
  showDeleteSuccessNotification,
  showSaveSuccessNotification,
} from "../utils/dialogs";
import {showPrintPopup} from "../print/PrintPopup";
import PrintMaintenanceSheetPage from "../print/PrintMaintenanceSheetPage";
import {getCostBearerFilters, renderHandledIcon, renderMaintenanceNameCell} from "./MaintenanceUtils";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";
import ButtonBar from "../common/ButtonBar";
import InvoiceCell from "../invoicing/InvoiceCell";

interface State extends GridState<any> {
  error?: ErrorResponse;
}

const initialGridState = (): GridState<MaintenanceHistory> => {
  return {
    ...tableUtils.initialGridState(20),
  }
};

class MaintenanceSheetsPage extends React.Component<RouteComponentProps, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  public state: State = {
    // Default initial grid state
    ...initialGridState(),

    // Initial sorting
    sortColumnKey: 'date',
    sortOrder: 'descend',

    // Override with cached state from Router
    ...this.props.location.state,
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {error} = this.state;

    const columns: Array<ColumnProps<MaintenanceHistory>> = [
      {
        ...sortableColumn('', 'handled', this.state, this.renderHandledCell),
        width: 32,
      },
      sortableColumn(locales.maintenance_date, 'date', this.state, r => formatDate(r.date)),
      searchableSortableColumn(locales.client, 'clientName', this.state, this.renderClientCell),
      searchableSortableColumn(locales.item, 'itemName', this.state, this.renderItemCell),
      searchableSortableColumn(locales.inventoryNumber, 'itemInventoryNumber', this.state, (r) => r.item?.inventoryNumber ?? ''),
      searchableSortableColumn(locales.appellation, 'name', this.state, renderMaintenanceNameCell),
      filterableColumn(locales.cost_bearer, 'costBearer', this.state, this.renderCostBearerCell, getCostBearerFilters(), false),
      sortableColumn(locales.total, 'totalCost', this.state, (r) => renderPriceColumn(r.totalCost)),
      simpleColumn(locales.invoice, this.renderInvoice),
      {key: 'actions', width: '141px', render: this.renderActionCell},
    ];
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.maintenance, this)}
        </GlobalAlertContextConsumer>

        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus"
                  onClick={this.onAddMaintenanceClick}>{locales.add}</Button>
        </ButtonBar>


        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderHandledCell = (record: MaintenanceHistory) => {
    return renderHandledIcon(record.handled);
  }

  private renderClientCell = (record: MaintenanceHistory) => {
    const {client} = record;
    return client ? <Link to={paths.viewClientPath(client.id)}>{client.name}</Link> : "";
  };

  private renderItemCell = (record: MaintenanceHistory) => {
    if (!record.item) {
      return '';
    }

    return <Link to={paths.viewItemPath(record.item!.id)}>
      {renderItemName(record.item!)}
    </Link>;
  };

  private renderCostBearerCell = (record: MaintenanceHistory) => {
    const {costBearer} = record;
    if (costBearer === COST_BEARER_OWN) {
      return <>{locales.cost_own_short}</>
    } else if (costBearer === COST_BEARER_RENTER) {
      return <>{locales.cost_renter_short}</>
    } else {
      return <>&nbsp;</>
    }
  };

  private renderInvoice = (record: MaintenanceHistory) => {
    return <InvoiceCell finalized={record.finalized} invoice={record.invoice}/>;
  };

  private renderActionCell = (record: MaintenanceHistory) => {
    const {handled} = record;
    const handleTitle = handled ? locales.reopen : locales.handle;
    const handleIcon = handled ? 'file-exclamation' : 'file-done';
    const handleConfirm = handled ? locales.handle_item_maintenance_reopen : locales.handle_item_maintenance_close;
    const onPrintClick = () => this.printHistoryItem(record);
    const onHandleClick = () => {
      showConfirm(handleConfirm, () => this.handleHistoryItem(record));
    }
    const onEditClick = () => this.openHistoryItem(record);
    const onDeleteClick = () => {
      showDeleteConfirm(() => this.deleteHistoryItem(record));
    }

    return (
      <div className="action-column" style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
        <ButtonGroup>
          <Button icon="printer" title={locales.print} disabled={!record.item} onClick={onPrintClick}/>
          <Button icon={handleIcon} title={handleTitle} onClick={onHandleClick}/>
          <Button icon="edit" title={locales.edit} onClick={onEditClick}/>
          <Button icon="delete" title={locales.delete} onClick={onDeleteClick} disabled={handled}/>
        </ButtonGroup>
      </div>
    );
  };

  private onGridChange = (newState: GridState<MaintenanceHistory>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    this.loadMaintenanceHistory();
  };

  private loadMaintenanceHistory = () => {
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.maintenance.getAllMaintenanceHistory(request)
      .then(this.onGridSuccess)
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onGridSuccess = (response: PageResponseEnvelope<MaintenanceHistory>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onError = (error: ErrorResponse) => {
    this.setState({error})
  };

  private printHistoryItem = (historyItem: MaintenanceHistory) => {
    if (!historyItem.item) {
      return
    }

    showPrintPopup(
      locales.maintenance_worksheet,
      <PrintMaintenanceSheetPage historyItem={historyItem} item={historyItem.item!}/>
    )
  }

  private handleHistoryItem = (historyItem: MaintenanceHistory) => {
    const historyItemId = historyItem.id;
    if (!historyItemId) {
      return;
    }
    const promise = furoApi.maintenance.handleMaintenanceHistoryItem(historyItemId, !historyItem.handled)
      .then(this.onHandleSuccess)
      .catch(this.onError)
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onHandleSuccess = () => {
    showSaveSuccessNotification();
    this.alertController?.refreshAlertCount();
    this.reload();
  }

  private deleteHistoryItem = (historyItem: MaintenanceHistory) => {
    const historyItemId = historyItem.id;

    if (!historyItemId) {
      return;
    }

    const promise = furoApi.maintenance.deleteMaintenanceHistoryItem(historyItemId)
      .then(this.onDeleteSuccess)
      .catch(this.onError)
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onDeleteSuccess = () => {
    showDeleteSuccessNotification();
    this.reload();
  }

  private onAddMaintenanceClick = () => {
    this.props.history.push(paths.createMaintenanceHistoryItemPathBase);
  }

  private openHistoryItem = (historyItem: MaintenanceHistory) => {
    this.props.history.push(paths.viewMaintenanceHistoryItemPath(historyItem.id!));
  }
}

export default withRouter(MaintenanceSheetsPage);


// WEBPACK FOOTER //
// ./src/maintenance/MaintenanceSheetsPage.tsx